import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { usePreviousValue } from '@frontend/design-system';
import { useCalendarStore } from '../../stores/calendar.store';
import { getDateString } from '../../utils/date-helpers';

export const useCurrentDateQueryParam = () => {
  const navigate = useNavigate();
  const { calendarDate, setCalendarDate } = useCalendarStore(['calendarDate', 'setCalendarDate']);
  const { currentDate, ...search } = useSearch({ strict: false }) as {
    currentDate?: string;
  };

  const [previousCalendarDate, currentCalendarDate] = usePreviousValue(calendarDate);
  const [previousCurrentDate, currentCurrentDate] = usePreviousValue(currentDate);
  const hasCalendarDateChanged = previousCalendarDate !== currentCalendarDate;
  const hasCurrentDateChanged = previousCurrentDate !== currentCurrentDate;

  const normalizedCurrentDate = (() => {
    if (!currentDate) {
      return undefined;
    }

    try {
      return decodeURIComponent(currentDate);
    } catch {
      return currentDate;
    }
  })();

  useEffect(() => {
    const calendarDateParamDate = getDateString(normalizedCurrentDate);
    if (calendarDateParamDate) {
      setCalendarDate(calendarDateParamDate);
    }
  }, [setCalendarDate, normalizedCurrentDate]);

  // Remove the currentDate query param when calendarDate changes due to user interaction,
  // to prevent conflicts between the two sources of truth for the date (query param vs calendar store)
  useEffect(() => {
    if (
      hasCalendarDateChanged &&
      !hasCurrentDateChanged &&
      !dayjs(currentCurrentDate).isSame(dayjs(currentCalendarDate), 'day')
    ) {
      navigate({
        to: `/schedule/calendar`,
        replace: true,
        search: { ...search, currentDate: undefined, eventId: undefined },
      });
    }
  }, [navigate, hasCalendarDateChanged, hasCurrentDateChanged, currentCurrentDate, currentCalendarDate, search]);
};
